import { SetStateAction, useContext, useEffect, useState } from "react"
import { AdminContext } from "../../../../../../contexts/AdminContext"
import { ModalContext } from "../../../../../../contexts/admin/ModalContext"
import CampsDropdown from "../Fields/CampsDropdown"
import Button from "~/components/ui/Button"
import { CheckIcon, LinkIcon, NoSymbolIcon, XMarkIcon } from "@heroicons/react/24/solid"
import eAdminUserRole from "~/enums/eAdminUserRole"
import copyText from "~/utils/copyText"
import baseUrl from "~/utils/admin/baseUrl"
import { UserModalFormProps, UserModalProps } from "../../interfaces/UserInterface"
import { useUser } from "../../hooks/useUser"
import Spinner from "~/components/ui/Spinner"
import { useForm } from "react-hook-form"
import UserAccessOptionsSelector from "../Fields/ReservationOptionsSelector"
import Form from "~/components/ui/Admin/Form"
import { FormProvider } from "~/contexts/FormContext"
import useAdminForm from "~/hooks/useAdminForm"
import { getDefaultValues } from "../../helpers/userHelper"
import { createUserId } from "../../constants/UserModalId"
import { AdminUserStatuses } from "../../constants/AdminUserStatuses"
import { cn } from "~/utils/tailwind"
import { IAdminUser } from "~/components/Admin/Settings/Users/interfaces/AdminUserInterface"
import isValidEmail from "~/utils/isValidEmail"

const UserModalContent = ({ selectedUser, setUsers }: UserModalProps) => {
  const reactHookForm = useForm({ defaultValues: getDefaultValues(selectedUser) })

  return (
    <FormProvider init={{ ...reactHookForm.watch() }}>
      <UserModalForm selectedUser={selectedUser} setUsers={setUsers} />
    </FormProvider>
  )
}

const UserModalForm = ({ selectedUser, setUsers }: UserModalFormProps) => {
  const { closeModal, accessibleCamps, camp, showNotification } = useContext(AdminContext)

  const [isRemoveButtonClicked, setIsRemoveButtonClicked] = useState(false)

  const [isForgotButtonClicked, setIsForgotButtonClicked] = useState(false)
  const [isSendLinkButtonClicked, setIsSendLinkButtonClicked] = useState(false)

  const { setValue, isDirty, watch, handleSubmit } = useAdminForm()

  const { firstName, lastName, email, role, selectedCampIds } = watch()

  const {
    isLoading,
    update: handleUpdateUserMutation,
    inviteUser: handleInviteUserMutation,
    resendInviteUser: handleResendInviteUserMutation,
    forgotPassword: handleForgotPasswordMutation,
    destroy: handleDeleteUserMutation,
  } = useUser({camp_id: camp.id})

  const resendUserInvitation = (data: IAdminUser) => {
    setIsSendLinkButtonClicked(true)
    handleResendInviteUserMutation({
      selectedUserId: data.id,
      onSuccess: () => showNotification({ type: "success", title: "Invitation sent" }),
    })
  }

  const deleteUser = () => {
    if (isRemoveButtonClicked) {
      closeModal(createUserId)
      handleDeleteUserMutation({
        user_id: selectedUser?.id,
        setUsers,
        onSuccess: () => showNotification({ type: "success", title: "User removed successfully" }),
      })
    } else {
      setIsRemoveButtonClicked(true)
    }
  }

  const setSelectedCampIds = (v: string[]) => setValue("selectedCampIds", v)
  const setAccess = (v: eAdminUserRole) => setValue("role", v)

  const uniqueById = <T extends { id: number | string }>(arr: T[]): T[] => [
    ...new Map(arr.map((item) => [item.id, item])).values(),
  ]

  const onSubmit = () => {

    if(!isValidEmail(email)){
      showNotification({
        type: "error",
        title: "Failed to create user",
        description: "Invalid email address",
      })

      return
    }

    if (!!selectedUser) {
      const camps = uniqueById([...selectedUser.camps, ...(!!accessibleCamps ? accessibleCamps : [])]).filter((camp) =>
        selectedCampIds.includes(camp.id),
      )
      const updatedUserParams = {
        selectedUserId: selectedUser?.id,
        first_name: firstName,
        last_name: lastName,
        full_name: `${firstName} ${lastName}`,
        email,
        camp_ids: selectedCampIds,
        camps,
        status: selectedUser.status,
        role,
      }

      handleUpdateUserMutation({
        onSuccess: () => {
          closeModal()
          setUsers((prev) => {
            const selectedUserIndex = prev?.findIndex((user) => user.id === selectedUser.id)
            const newArr = [...prev]

            if (selectedUserIndex >= 0) {
              newArr[selectedUserIndex] = {
                ...newArr[selectedUserIndex],
                ...updatedUserParams,
              }
            }

            return newArr
          })
        },
        ...updatedUserParams,
      })
    } else {
      handleInviteUserMutation({
        onSuccess: () => {
          closeModal()
          showNotification({ type: "success", title: "User invited successfully" })
        },
        onError: (error) => {
          showNotification({
            type: "error",
            title: "Failed to create user",
            description: error.message || "An unexpected error occurred",
          })
        },
        first_name: firstName,
        last_name: lastName,
        email,
        camp_ids: selectedCampIds,
        role,
        selectedUserId: selectedUser?.id,
        status: AdminUserStatuses.STATUS_INVITED,
        camp_id: camp.id,
      })
    }
  }

  return (
    <form className="flex flex-col justify-between w-full" onSubmit={handleSubmit(onSubmit)}>
      {isLoading && (
        <div className="fixed z-50 inset-0 bg-black bg-opacity-35 flex items-center justify-center">
          <Spinner size={12} />
        </div>
      )}
      <div className="font-semibold text-xl pb-4 mb-1 border-b border-gray-200">
        {selectedUser ? "Edit" : "Create"} user
      </div>
      <div className="py-5 flex flex-col gap-5">
        <div className="flex gap-3">
          <Form.TextInput label="First name" className="flex-1" field="firstName" />
          <Form.TextInput label="Last name" className="flex-1" field="lastName" />
        </div>
        {((accessibleCamps && accessibleCamps?.length > 1 && !!selectedUser) || !selectedUser) && (
          <Form.TextInput
            disabled={!selectedUser?.invitation_accepted_at?.length && !!selectedUser}
            label="Email"
            className="flex-1"
            field="email"
            required
          />
        )}
        {((accessibleCamps && accessibleCamps?.length > 1 && !selectedUser) || !!selectedUser) && (
          <CampsDropdown
            options={
              accessibleCamps?.map((camp) => ({
                key: camp.id,
                value: camp.id,
                title: camp.name,
              })) || []
            }
            selectedOptions={selectedCampIds}
            setSelectedOptions={setSelectedCampIds}
          />
        )}
        <UserAccessOptionsSelector selectedAccess={role} setSelectedOption={setAccess} />
      </div>
      {!!selectedUser && (
        <div className="flex custom-500:justify-between flex-col items-center gap-2 custom-500:flex-row custom-500:items-start">
          <div className="flex gap-3 flex-col xs:flex-row">
            {selectedUser.invitation_accepted_at?.length ? (
              <Button
                onClick={() => handleForgotPasswordMutation({ email: selectedUser?.email, setIsForgotButtonClicked })}
                disabled={!email?.length || isForgotButtonClicked}
                className="flex border hover:bg-gray-200 border-gray-200 font-semibold gap-2"
                children={
                  <>
                    {isForgotButtonClicked ? <CheckIcon className="w-5" /> : <NoSymbolIcon className="w-5" />}
                    {isForgotButtonClicked ? <span>Email sent!</span> : <span>Forgot Password</span>}
                  </>
                }
                variant="none"
              />
            ) : (
              <Button
                onClick={() => resendUserInvitation(selectedUser)}
                className={cn(
                  "flex border hover:bg-gray-200 border-gray-200 font-semibold gap-2",
                  isSendLinkButtonClicked && "text-green-500",
                )}
                children={
                  <>
                    {isSendLinkButtonClicked ? <CheckIcon className="w-5" /> : <LinkIcon className="w-5" />}
                    {isSendLinkButtonClicked ? <span>Invitation sent</span> : <span>Resend invitation</span>}
                  </>
                }
                variant="none"
              />
            )}
            <Button
              onClick={deleteUser}
              className="text-red-500 hover:text-white hover:bg-red-500 border border-red-500 flex font-semibold gap-2"
              children={
                <>
                  <XMarkIcon className="w-5" />
                  {!isRemoveButtonClicked ? (
                    selectedUser.invitation_accepted_at?.length ? (
                      <span>Remove User</span>
                    ) : (
                      <span>Revoke invite</span>
                    )
                  ) : (
                    <span>Are you sure?</span>
                  )}
                </>
              }
              variant="none"
            />
          </div>
          <Button
            type="submit"
            disabled={!isDirty || isLoading}
            children={"Save"}
            variant="blue"
            className="px-10"
            style={{ padding: "9.75px 40px" }}
          />
        </div>
      )}
      {!selectedUser && (
        <div className="flex justify-end">
          <Button
            type="submit"
            children={"Send invite"}
            variant="blue"
            className="px-10"
            style={{ padding: "9.75px 40px" }}
            disabled={isLoading}
          />
        </div>
      )}
    </form>
  )
}

export default UserModalContent
